import { createSlice } from '@reduxjs/toolkit';
import { firstValueFrom } from 'rxjs';
import passengerService from '../services/passengerService';
import reservationService from '../services/reservationService';
import { PagedList } from './models/paged-list.model';
import {
  Passenger,
  PassengerAddress,
  PassengerPreference,
} from './models/passenger.model';
import { Reservation, TripLeg } from './models/reservation.model';
import * as uiSlice from './ui.slice';
import {
  TransportReservationF1FormattedResponse,
  TransportReservationRequest,
  TransportReservationResponse,
} from '@/data/models/transport-reservation.model';

type NextPageData = {
  hasMorePages: boolean;
  pageSize: number;
  nextPage?: number | null;
};

interface PassengerState {
  passengerSearchClientBC: string;
  passengerSearchText: string;
  /** Phone-number search, independent of the client + search-text pair. */
  passengerPhoneSearch: string;
  /**
   * Counts how often the search was replaced from outside the search UI. SearchToolbar puts it in
   * the key of its input fields so they remount and show the new value - TextSearch keeps its
   * value in local state and stops following the store after mount.
   */
  passengerSearchResetToken: number;
  passengerSearchPage: number;
  passengerSearchPageSize: number;
  passengers: PagedList<Passenger>;
  loadingPassengers: boolean;
  loadedPassengers: boolean;
  currentPassenger: Passenger | null;
  loadingCurrentPassenger: boolean;
  loadedCurrentPassenger: boolean;
  currentPassengerPreferences: PassengerPreference[];
  loadingCurrentPassengerPreferences: boolean;
  loadedCurrentPassengerPreferences: boolean;
  savingCurrentPassengerPreferences: boolean;
  savedCurrentPassengerPreferences: boolean;
  currentPassengerPastReservations: Reservation[];
  currentPassengerPastReservationsNextPageData?: NextPageData;
  loadingCurrentPassengerPastReservations: boolean;
  loadedCurrentPassengerPastReservations: boolean;
  currentPassengerFutureReservations: Reservation[];
  currentPassengerFutureReservationsNextPageData?: NextPageData;
  loadingCurrentPassengerFutureReservations: boolean;
  loadedCurrentPassengerFutureReservations: boolean;
  currentPassengerReservationLookup: Reservation | null;
  loadingCurrentPassengerReservationLookup: boolean;
  loadedCurrentPassengerReservationLookup: boolean;
  savingPassenger: boolean;
  savedPassenger: boolean;

  passengerEventEmitted: Passenger | null;
}

export const initialState: PassengerState = {
  passengerSearchClientBC: '',
  passengerSearchText: '',
  passengerPhoneSearch: '',
  passengerSearchResetToken: 0,
  passengerSearchPage: 1,
  passengerSearchPageSize: 20,
  passengers: { itemCount: 0, currentPage: 1, totalPages: 0, items: [] },
  loadingPassengers: false,
  loadedPassengers: false,
  currentPassenger: null,
  loadingCurrentPassenger: false,
  loadedCurrentPassenger: false,
  currentPassengerPreferences: [],
  loadingCurrentPassengerPreferences: false,
  loadedCurrentPassengerPreferences: false,
  savingCurrentPassengerPreferences: false,
  savedCurrentPassengerPreferences: false,
  currentPassengerPastReservations: [],
  currentPassengerPastReservationsNextPageData: undefined,
  loadingCurrentPassengerPastReservations: false,
  loadedCurrentPassengerPastReservations: false,
  currentPassengerFutureReservations: [],
  currentPassengerFutureReservationsNextPageData: undefined,
  loadingCurrentPassengerFutureReservations: false,
  loadedCurrentPassengerFutureReservations: false,
  currentPassengerReservationLookup: null,
  loadingCurrentPassengerReservationLookup: false,
  loadedCurrentPassengerReservationLookup: false,
  savingPassenger: false,
  savedPassenger: false,

  passengerEventEmitted: null,
};

const tripLegStatusCanceled = 'canceled';
const tripLegStatusCanceledLate = 'canceledlate';
const tripLegStatusBCCanceled = 'CANCELED';
const tripLegStatusBCCanceledLate = 'CANCELEDLATE';

const canceledTripLegStatuses = new Set<string>([
  tripLegStatusCanceled,
  tripLegStatusCanceledLate,
]);

const isCanceledTripLeg = (tripLeg: TripLeg) =>
  canceledTripLegStatuses.has(tripLeg.status?.toLowerCase() || '');

const sortTripLegsBySequence = (tripLegs: TripLeg[]) =>
  [...tripLegs].sort(
    (a, b) => (a.tripLegSequence || 0) - (b.tripLegSequence || 0),
  );

const filterMissingCanceledTripLegs = (
  incomingTripLegs: TripLeg[],
  existingTripLegs: TripLeg[],
) => {
  const incomingTripLegIDs = new Set(
    incomingTripLegs.map((tripLeg) => tripLeg.tripLegReferenceID),
  );

  return existingTripLegs.filter(
    (tripLeg) =>
      !incomingTripLegIDs.has(tripLeg.tripLegReferenceID) &&
      isCanceledTripLeg(tripLeg),
  );
};

const preserveExistingCanceledTripLegs = (
  incomingReservations: Reservation[],
  existingReservations: Reservation[],
) =>
  incomingReservations.map((incomingReservation) => {
    const existingReservation = existingReservations.find(
      (reservation) =>
        reservation.reservationReferenceID ===
          incomingReservation.reservationReferenceID &&
        reservation.passengerUUID === incomingReservation.passengerUUID,
    );
    if (!existingReservation) return incomingReservation;

    const missingCanceledTripLegs = filterMissingCanceledTripLegs(
      incomingReservation.tripLegs,
      existingReservation.tripLegs,
    );
    if (!missingCanceledTripLegs.length) return incomingReservation;

    return {
      ...incomingReservation,
      tripLegs: sortTripLegsBySequence([
        ...incomingReservation.tripLegs,
        ...missingCanceledTripLegs,
      ]),
    };
  });

const getNextPageData = (response: TransportReservationF1FormattedResponse) => {
  const hasMorePages = response.totalPages > response.currentPage;

  return {
    hasMorePages,
    nextPage: hasMorePages ? response.currentPage + 1 : null,
    pageSize: response.filters.pageSize!,
  };
};

export const slice = createSlice({
  name: 'passenger',
  initialState,
  reducers: {
    setPassengerSearchClientBC: (
      state: PassengerState,
      action: { type: string; payload: string },
    ) => {
      state.passengerSearchClientBC = action.payload;
    },
    setPassengerSearchText: (
      state: PassengerState,
      action: { type: string; payload: string },
    ) => {
      state.passengerSearchText = action.payload;
    },
    setPassengerPhoneSearch: (
      state: PassengerState,
      action: { type: string; payload: string },
    ) => {
      state.passengerPhoneSearch = action.payload;
    },
    /**
     * A phone search pushed in from outside the search UI - today an incoming call in the Amazon
     * Connect workspace.
     *
     * It replaces the whole search rather than adding to it: the client selection and the free
     * text go, because a phone lookup spans all clients and the API rejects the combination. The
     * reset token makes the change visible in the input fields, which hold their own state.
     */
    setPassengerSearchByPhone: (
      state: PassengerState,
      action: { type: string; payload: string },
    ) => {
      state.passengerPhoneSearch = action.payload;
      state.passengerSearchClientBC = '';
      state.passengerSearchText = '';
      state.passengerSearchPage = 1;
      state.passengerSearchResetToken += 1;
    },
    setPassengerSearchPage: (
      state: PassengerState,
      action: { type: string; payload: number },
    ) => {
      state.passengerSearchPage = action.payload;
    },
    setPassengerSearchPageSize: (
      state: PassengerState,
      action: { type: string; payload: number },
    ) => {
      state.passengerSearchPageSize = action.payload;
    },
    setPassengers: (
      state: PassengerState,
      action: { type: string; payload: PagedList<Passenger> },
    ) => {
      const list = action.payload;
      if (list.currentPage === 0) {
        list.currentPage = 1;
      }
      state.passengers = list;
    },
    clearPassengers: (state: PassengerState, action: { type: string }) => {
      state.passengers = {
        itemCount: 0,
        currentPage: 1,
        totalPages: 0,
        items: [],
      };
      state.loadingPassengers = false;
      state.loadedPassengers = false;
      state.passengerSearchPage = 1;
    },
    setLoadingPassengers: (
      state: PassengerState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingPassengers = action.payload;
    },
    setLoadedPassengers: (
      state: PassengerState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedPassengers = action.payload;
    },
    setCurrentPassenger: (
      state: PassengerState,
      action: { type: string; payload: Passenger | null },
    ) => {
      state.currentPassenger = action.payload;
    },
    setLoadingCurrentPassenger: (
      state: PassengerState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingCurrentPassenger = action.payload;
    },
    setLoadedCurrentPassenger: (
      state: PassengerState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedCurrentPassenger = action.payload;
    },
    setCurrentPassengerPreferences: (
      state: PassengerState,
      action: { type: string; payload: PassengerPreference[] },
    ) => {
      state.currentPassengerPreferences = action.payload;
    },
    setLoadingCurrentPassengerPreferences: (
      state: PassengerState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingCurrentPassengerPreferences = action.payload;
    },
    setLoadedCurrentPassengerPreferences: (
      state: PassengerState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedCurrentPassengerPreferences = action.payload;
    },
    setSavingCurrentPassengerPreferences: (
      state: PassengerState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingCurrentPassengerPreferences = action.payload;
    },
    setSavedCurrentPassengerPreferences: (
      state: PassengerState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedCurrentPassengerPreferences = action.payload;
    },
    setCurrentPassengerPastReservations: (
      state: PassengerState,
      action: {
        type: string;
        payload: TransportReservationF1FormattedResponse;
      },
    ) => {
      state.currentPassengerPastReservations = action.payload.items;
    },
    appendCurrentPassengerPastReservations: (
      state: PassengerState,
      action: {
        type: string;
        payload: TransportReservationF1FormattedResponse;
      },
    ) => {
      state.currentPassengerPastReservations = [
        ...state.currentPassengerPastReservations,
        ...action.payload.items,
      ];
    },
    setLoadingCurrentPassengerPastReservations: (
      state: PassengerState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingCurrentPassengerPastReservations = action.payload;
    },
    setLoadedCurrentPassengerPastReservations: (
      state: PassengerState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedCurrentPassengerPastReservations = action.payload;
    },
    setCurrentPassengerPastReservationsNextPageData: (
      state,
      action: { payload: TransportReservationF1FormattedResponse },
    ) => {
      state.currentPassengerPastReservationsNextPageData = getNextPageData(
        action.payload,
      );
    },
    setCurrentPassengerFutureReservations: (
      state: PassengerState,
      action: {
        type: string;
        payload: TransportReservationF1FormattedResponse;
      },
    ) => {
      state.currentPassengerFutureReservations =
        preserveExistingCanceledTripLegs(
          action.payload.items,
          state.currentPassengerFutureReservations,
        );
    },
    appendCurrentPassengerFutureReservations: (
      state: PassengerState,
      action: {
        type: string;
        payload: TransportReservationF1FormattedResponse;
      },
    ) => {
      state.currentPassengerFutureReservations = [
        ...state.currentPassengerFutureReservations,
        ...action.payload.items,
      ];
    },
    setLoadingCurrentPassengerFutureReservations: (
      state: PassengerState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingCurrentPassengerFutureReservations = action.payload;
    },
    setLoadedCurrentPassengerFutureReservations: (
      state: PassengerState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedCurrentPassengerFutureReservations = action.payload;
    },
    setCurrentPassengerFutureReservationsNextPageData: (
      state,
      action: { payload: TransportReservationF1FormattedResponse },
    ) => {
      state.currentPassengerFutureReservationsNextPageData = getNextPageData(
        action.payload,
      );
    },
    setCurrentPassengerReservationLookup: (
      state: PassengerState,
      action: { type: string; payload: Reservation | null },
    ) => {
      state.currentPassengerReservationLookup = action.payload;
    },
    setLoadingCurrentPassengerReservationLookup: (
      state: PassengerState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadingCurrentPassengerReservationLookup = action.payload;
    },
    setLoadedCurrentPassengerReservationLookup: (
      state: PassengerState,
      action: { type: string; payload: boolean },
    ) => {
      state.loadedCurrentPassengerReservationLookup = action.payload;
    },
    clearCurrentPassengerReservationLookup: (
      state: PassengerState,
      _action: { type: string },
    ) => {
      state.currentPassengerReservationLookup = null;
      state.loadingCurrentPassengerReservationLookup = false;
      state.loadedCurrentPassengerReservationLookup = false;
    },
    mergeTripLegIntoReservationLookup: (
      state: PassengerState,
      action: { type: string; payload: TripLeg },
    ) => {
      if (!state.currentPassengerReservationLookup) return;
      const existing = state.currentPassengerReservationLookup.tripLegs;
      const alreadyPresent = existing.some(
        (l) => l.tripLegReferenceID === action.payload.tripLegReferenceID,
      );
      if (alreadyPresent) return;
      const merged = [...existing, action.payload].sort(
        (a, b) => (a.tripLegSequence || 0) - (b.tripLegSequence || 0),
      );
      state.currentPassengerReservationLookup = {
        ...state.currentPassengerReservationLookup,
        tripLegs: merged,
      };
    },
    addFuturePassengerReservation: (
      state: PassengerState,
      action: { type: string; payload: Reservation },
    ) => {
      state.currentPassengerFutureReservations = [
        action.payload,
        ...state.currentPassengerFutureReservations,
      ];
    },
    addPastPassengerReservation: (
      state: PassengerState,
      action: { type: string; payload: Reservation },
    ) => {
      state.currentPassengerPastReservations = [
        action.payload,
        ...state.currentPassengerPastReservations,
      ];
    },
    addFutureTripLeg: (
      state: PassengerState,
      action: {
        type: string;
        payload: { reservationReferenceID: string; tripLeg: TripLeg };
      },
    ) => {
      const reservation = state.currentPassengerFutureReservations.find(
        (r) =>
          r.reservationReferenceID === action.payload.reservationReferenceID,
      );
      if (reservation) {
        reservation.tripLegs.push(action.payload.tripLeg);
      }
    },
    updateFutureTripLegs: (
      state: PassengerState,
      action: {
        type: string;
        payload: TripLeg[];
      },
    ) => {
      const reservation = state.currentPassengerFutureReservations.find(
        (r) =>
          r.reservationReferenceID === action.payload[0].reservationReferenceID,
      );
      if (reservation) {
        const oldLegs = [...reservation.tripLegs];
        const newLegs = action.payload;
        newLegs.forEach((newLeg) => {
          const idx = oldLegs.findIndex(
            (l) => l.tripLegReferenceID === newLeg.tripLegReferenceID,
          );
          if (idx >= 0) {
            oldLegs.splice(idx, 1, newLeg);
          }
        });
        reservation.tripLegs = oldLegs;
      }
    },
    replaceReservation: (
      state: PassengerState,
      action: {
        type: string;
        payload: Reservation;
      },
    ) => {
      const reservations = state.currentPassengerFutureReservations;
      const idx = reservations.findIndex(
        (r) =>
          r.reservationReferenceID === action.payload.reservationReferenceID,
      );
      if (idx >= 0) {
        reservations.splice(idx, 1, action.payload);
      }
    },
    replaceReservationAfterCancelTripLeg: (
      state: PassengerState,
      action: {
        type: string;
        payload: {
          reservation: Reservation;
          tripLegReferenceID: string;
        };
      },
    ) => {
      const reservations = state.currentPassengerFutureReservations;
      const idx = reservations.findIndex(
        (r) =>
          r.reservationReferenceID ===
          action.payload.reservation.reservationReferenceID,
      );
      if (idx < 0) return;

      const existingReservation = reservations[idx];
      const responseTripLegs = action.payload.reservation.tripLegs || [];
      const responseTripLegIDs = new Set(
        responseTripLegs.map((l) => l.tripLegReferenceID),
      );
      const missingExistingTripLegs = existingReservation.tripLegs.filter(
        (l) => !responseTripLegIDs.has(l.tripLegReferenceID),
      );
      const tripLegs = [...responseTripLegs, ...missingExistingTripLegs].map(
        (tripLeg) => {
          if (
            tripLeg.tripLegReferenceID !== action.payload.tripLegReferenceID
          ) {
            return tripLeg;
          }

          const status = tripLeg.status?.toLowerCase();
          const isCanceledLate = status === tripLegStatusCanceledLate;
          const canceledStatus = isCanceledLate
            ? tripLegStatusCanceledLate
            : tripLegStatusCanceled;

          return {
            ...tripLeg,
            status: canceledStatus,
            tripLegStatusBC: isCanceledLate
              ? tripLegStatusBCCanceledLate
              : tripLegStatusBCCanceled,
          };
        },
      );

      reservations.splice(idx, 1, {
        ...action.payload.reservation,
        tripLegs: sortTripLegsBySequence(tripLegs),
      });
    },
    removeReservation: (
      state: PassengerState,
      action: {
        type: string;
        payload: string;
      },
    ) => {
      const reservations = state.currentPassengerFutureReservations;
      const idx = reservations.findIndex(
        (r) => r.reservationReferenceID === action.payload,
      );
      reservations.splice(idx, 1);
    },
    removeStandingOrder: (
      state: PassengerState,
      action: {
        type: string;
        payload: string;
      },
    ) => {
      const stoUUID = action.payload;
      const reservations = state.currentPassengerFutureReservations;
      state.currentPassengerFutureReservations = reservations.filter((r) => {
        r.standingOrderUUID !== stoUUID;
      });
    },
    replaceTripLegs: (
      state: PassengerState,
      action: {
        type: string;
        payload: { reservationReferenceID: string; legOrder: TripLeg[] };
      },
    ) => {
      const reservation = state.currentPassengerFutureReservations.find(
        (r) =>
          r.reservationReferenceID === action.payload.reservationReferenceID,
      );
      if (reservation) {
        reservation.tripLegs = action.payload.legOrder;
      }
    },
    setSavingPassenger: (
      state: PassengerState,
      action: { type: string; payload: boolean },
    ) => {
      state.savingPassenger = action.payload;
    },
    setSavedPassenger: (
      state: PassengerState,
      action: { type: string; payload: boolean },
    ) => {
      state.savedPassenger = action.payload;
    },
    updateTripLegInReservation: (
      state: PassengerState,
      action: {
        type: string;
        payload: {
          reservationReferenceID: string;
          tripLegReferenceID: string;
          transportationProviderID: number;
          status: string;
        };
      },
    ) => {
      const reservationIndex =
        state.currentPassengerFutureReservations.findIndex(
          (r) =>
            r.reservationReferenceID === action.payload.reservationReferenceID,
        );
      if (reservationIndex >= 0) {
        const reservation =
          state.currentPassengerFutureReservations[reservationIndex];
        const tripLegIndex = reservation.tripLegs.findIndex(
          (tl: TripLeg) =>
            tl.tripLegReferenceID === action.payload.tripLegReferenceID,
        );
        if (tripLegIndex >= 0) {
          const tripLeg = {
            ...reservation.tripLegs[tripLegIndex],
            status: action.payload.status,
            transportationProviderID: action.payload.transportationProviderID,
          };
          const tripLegs = [...reservation.tripLegs];
          tripLegs.splice(tripLegIndex, 1, tripLeg);
          const updatedReservation = { ...reservation, tripLegs };
          const reservations = [...state.currentPassengerFutureReservations];
          reservations.splice(reservationIndex, 1, updatedReservation);
          state.currentPassengerFutureReservations = reservations;
        }
      }
    },
    setPassengerEventEmitted: (
      state: PassengerState,
      action: { type: string; payload: Passenger | null },
    ) => {
      state.passengerEventEmitted = action.payload;
    },
    clearCurrentPassengerReservations: (state: PassengerState) => {
      state.currentPassengerFutureReservations = [];
      state.currentPassengerFutureReservationsNextPageData = undefined;
      state.loadingCurrentPassengerFutureReservations = false;
      state.loadedCurrentPassengerFutureReservations = false;
      state.currentPassengerPastReservations = [];
      state.currentPassengerPastReservationsNextPageData = undefined;
      state.loadingCurrentPassengerPastReservations = false;
      state.loadedCurrentPassengerPastReservations = false;
    },
  },
});

export const { setPassengerSearchClientBC } = slice.actions;
export const { setPassengerSearchText } = slice.actions;
export const { setPassengerPhoneSearch } = slice.actions;
export const { setPassengerSearchByPhone } = slice.actions;
export const { setPassengerSearchPage } = slice.actions;
export const { setPassengerSearchPageSize } = slice.actions;
export const { setPassengers } = slice.actions;
export const { clearPassengers } = slice.actions;
export const { setLoadingPassengers } = slice.actions;
export const { setLoadedPassengers } = slice.actions;
export const { setCurrentPassenger } = slice.actions;
export const { setLoadingCurrentPassenger } = slice.actions;
export const { setLoadedCurrentPassenger } = slice.actions;
export const { setCurrentPassengerPreferences } = slice.actions;
export const { setLoadingCurrentPassengerPreferences } = slice.actions;
export const { setLoadedCurrentPassengerPreferences } = slice.actions;
export const { setSavingCurrentPassengerPreferences } = slice.actions;
export const { setSavedCurrentPassengerPreferences } = slice.actions;
export const { setCurrentPassengerPastReservations } = slice.actions;
export const { appendCurrentPassengerPastReservations } = slice.actions;
export const { setLoadingCurrentPassengerPastReservations } = slice.actions;
export const { setLoadedCurrentPassengerPastReservations } = slice.actions;
export const { setCurrentPassengerPastReservationsNextPageData } =
  slice.actions;
export const { setCurrentPassengerFutureReservations } = slice.actions;
export const { appendCurrentPassengerFutureReservations } = slice.actions;
export const { setLoadingCurrentPassengerFutureReservations } = slice.actions;
export const { setLoadedCurrentPassengerFutureReservations } = slice.actions;
export const { setCurrentPassengerFutureReservationsNextPageData } =
  slice.actions;
export const { setCurrentPassengerReservationLookup } = slice.actions;
export const { setLoadingCurrentPassengerReservationLookup } = slice.actions;
export const { setLoadedCurrentPassengerReservationLookup } = slice.actions;
export const { clearCurrentPassengerReservationLookup } = slice.actions;
export const { mergeTripLegIntoReservationLookup } = slice.actions;
export const { addFuturePassengerReservation } = slice.actions;
export const { addPastPassengerReservation } = slice.actions;
export const { addFutureTripLeg } = slice.actions;
export const { updateFutureTripLegs } = slice.actions;
export const { replaceReservation } = slice.actions;
export const { replaceReservationAfterCancelTripLeg } = slice.actions;
export const { removeReservation } = slice.actions;
export const { replaceTripLegs } = slice.actions;
export const { setSavingPassenger } = slice.actions;
export const { setSavedPassenger } = slice.actions;
export const { updateTripLegInReservation } = slice.actions;
export const { setPassengerEventEmitted } = slice.actions;
export const { clearCurrentPassengerReservations } = slice.actions;

export const fetchAllPassengersAsync =
  (page: number, pageSize: number) => (dispatch: Function) => {
    const passengers = {
      itemCount: 0,
      currentPage: 1,
      totalPages: 0,
      items: [],
    };
    dispatch(setPassengers(passengers));
    dispatch(setLoadedPassengers(false));
    passengerService.getAllPassengers(page, pageSize).subscribe({
      next: (passengers) => {
        dispatch(setPassengers(passengers));
        dispatch(setLoadingPassengers(false));
        dispatch(setLoadedPassengers(true));
      },
      error: (err) => {
        dispatch(setLoadingPassengers(false));
        dispatch(setLoadedPassengers(false));
      },
    });
  };

export const fetchPassengersAsync =
  (clientBC: string, searchText: string, page: number, pageSize: number) =>
  (dispatch: Function) => {
    dispatch(setLoadingPassengers(true));
    dispatch(setLoadedPassengers(false));
    passengerService
      .getPassengers(clientBC, searchText, page, pageSize)
      .subscribe({
        next: (passengers) => {
          dispatch(setPassengers(passengers));
          dispatch(setLoadingPassengers(false));
          dispatch(setLoadedPassengers(true));
        },
        error: (err) => {
          dispatch(setLoadingPassengers(false));
          dispatch(setLoadedPassengers(false));
        },
      });
  };

/**
 * Search by phone number across every client.
 *
 * No clientBC is passed. The API allows that for a phone-only lookup and the database applies a
 * six-digit floor for it, so a shorter fragment returns a validation error rather than a page of
 * unrelated passengers.
 */
export const fetchPassengersByPhoneAsync =
  (phone: string, page: number, pageSize: number) => (dispatch: Function) => {
    dispatch(setLoadingPassengers(true));
    dispatch(setLoadedPassengers(false));
    passengerService.getPassengersByPhone(phone, page, pageSize).subscribe({
      next: (passengers) => {
        dispatch(setPassengers(passengers));
        dispatch(setLoadingPassengers(false));
        dispatch(setLoadedPassengers(true));
      },
      error: () => {
        dispatch(setLoadingPassengers(false));
        dispatch(setLoadedPassengers(false));
      },
    });
  };

export const fetchPassengerAsync =
  (id: string, signal?: AbortSignal) => (dispatch: Function) => {
    dispatch(setLoadingCurrentPassenger(true));
    dispatch(setLoadedCurrentPassenger(false));
    dispatch(setCurrentPassenger(null));
    passengerService.getPassenger(id, signal).subscribe({
      next: (passenger) => {
        dispatch(setCurrentPassenger(passenger));
        dispatch(setLoadingCurrentPassenger(false));
        dispatch(setLoadedCurrentPassenger(true));
      },
      error: (err) => {
        dispatch(setLoadingCurrentPassenger(false));
        dispatch(setLoadedCurrentPassenger(false));
      },
    });
  };

export const fetchPassengerPreferencesAsync =
  (id: string, clientBC?: string) => (dispatch: Function) => {
    dispatch(setLoadingCurrentPassengerPreferences(true));
    dispatch(setLoadedCurrentPassengerPreferences(false));
    dispatch(setCurrentPassengerPreferences([]));
    passengerService.getPassengerPreferences(id, clientBC).subscribe({
      next: (prefs) => {
        dispatch(setCurrentPassengerPreferences(prefs));
        dispatch(setLoadingCurrentPassengerPreferences(false));
        dispatch(setLoadedCurrentPassengerPreferences(true));
      },
      error: (err) => {
        dispatch(setLoadingCurrentPassengerPreferences(false));
        dispatch(setLoadedCurrentPassengerPreferences(false));
      },
    });
  };

export const savePassengerPreferencesAsync =
  (id: string, prefs: PassengerPreference[]) => (dispatch: Function) => {
    dispatch(setSavingCurrentPassengerPreferences(true));
    dispatch(setSavedCurrentPassengerPreferences(false));
    passengerService.savePassengerPreferences(id, prefs).subscribe({
      next: (updatedPrefs) => {
        dispatch(setCurrentPassengerPreferences(updatedPrefs));
        dispatch(setSavingCurrentPassengerPreferences(false));
        dispatch(setSavedCurrentPassengerPreferences(true));
      },
      error: (err) => {
        dispatch(setSavingCurrentPassengerPreferences(false));
        dispatch(setSavedCurrentPassengerPreferences(false));
      },
    });
  };

export const fetchPassengerPastReservationsAsync =
  (request: TransportReservationRequest) => (dispatch: Function) => {
    dispatch(setLoadingCurrentPassengerPastReservations(true));
    dispatch(setLoadedCurrentPassengerPastReservations(false));
    reservationService.getPassengerReservations(request).subscribe({
      next: (response) => {
        dispatch(setCurrentPassengerPastReservations(response));
        dispatch(setCurrentPassengerPastReservationsNextPageData(response));
        dispatch(setLoadingCurrentPassengerPastReservations(false));
        dispatch(setLoadedCurrentPassengerPastReservations(true));
      },
      error: (err) => {
        dispatch(setLoadingCurrentPassengerPastReservations(false));
        dispatch(setLoadedCurrentPassengerPastReservations(false));
      },
    });
  };

export const fetchPassengerFutureReservationsAsync =
  (request: TransportReservationRequest) => (dispatch: Function) => {
    dispatch(setLoadingCurrentPassengerFutureReservations(true));
    dispatch(setLoadedCurrentPassengerFutureReservations(false));
    reservationService.getPassengerReservations(request).subscribe({
      next: (response) => {
        dispatch(setCurrentPassengerFutureReservations(response));
        dispatch(setCurrentPassengerFutureReservationsNextPageData(response));
        dispatch(setLoadingCurrentPassengerFutureReservations(false));
        dispatch(setLoadedCurrentPassengerFutureReservations(true));
      },
      error: (err) => {
        dispatch(setLoadingCurrentPassengerFutureReservations(false));
        dispatch(setLoadedCurrentPassengerFutureReservations(false));
      },
    });
  };

export const loadMorePassengerPastReservationsAsync =
  (request: TransportReservationRequest) =>
  (dispatch: Function, getState: Function) => {
    const nextPageData =
      getState()?.passenger?.currentPassengerPastReservationsNextPageData;
    if (!nextPageData?.hasMorePages) return;

    dispatch(setLoadingCurrentPassengerPastReservations(true));
    reservationService
      .getPassengerReservations({
        ...request,
        page: nextPageData?.nextPage,
        pageSize: nextPageData?.pageSize,
      })
      .subscribe({
        next: (response) => {
          dispatch(appendCurrentPassengerPastReservations(response));
          dispatch(setCurrentPassengerPastReservationsNextPageData(response));
          dispatch(setLoadingCurrentPassengerPastReservations(false));
        },
        error: (err) => {
          dispatch(setLoadingCurrentPassengerPastReservations(false));
        },
      });
  };

export const loadMorePassengerFutureReservationsAsync =
  (request: TransportReservationRequest) =>
  (dispatch: Function, getState: Function) => {
    const nextPageData =
      getState()?.passenger?.currentPassengerFutureReservationsNextPageData;
    if (!nextPageData?.hasMorePages) return;

    dispatch(setLoadingCurrentPassengerFutureReservations(true));
    reservationService
      .getPassengerReservations({
        ...request,
        page: nextPageData?.nextPage,
        pageSize: nextPageData?.pageSize,
      })
      .subscribe({
        next: (response) => {
          dispatch(appendCurrentPassengerFutureReservations(response));
          dispatch(setCurrentPassengerFutureReservationsNextPageData(response));
          dispatch(setLoadingCurrentPassengerFutureReservations(false));
        },
        error: (err) => {
          dispatch(setLoadingCurrentPassengerFutureReservations(false));
        },
      });
  };

export const fetchPassengerReservationByReferenceAsync =
  (
    passengerUUID: string,
    reservationReferenceID: string,
    fallbackErrorMessage?: string,
  ) =>
  async (dispatch: Function) => {
    dispatch(setLoadingCurrentPassengerReservationLookup(true));
    dispatch(setLoadedCurrentPassengerReservationLookup(false));
    try {
      const reservation = await firstValueFrom(
        reservationService.getReservationByReferenceID(
          reservationReferenceID,
          passengerUUID,
        ),
      );
      dispatch(setCurrentPassengerReservationLookup(reservation));
      dispatch(setLoadingCurrentPassengerReservationLookup(false));
      dispatch(setLoadedCurrentPassengerReservationLookup(true));
    } catch (err: any) {
      dispatch(setLoadingCurrentPassengerReservationLookup(false));
      dispatch(setLoadedCurrentPassengerReservationLookup(false));
      dispatch(
        uiSlice.setErrorMessage(err?.message || fallbackErrorMessage || ''),
      );
    }
  };

export const fetchMissingTripLegIntoReservationLookupAsync =
  (
    passengerUUID: string,
    reservationReferenceID: string,
    tripLegReferenceID: string,
  ) =>
  async (dispatch: Function) => {
    try {
      const tripLeg = await firstValueFrom(
        reservationService.getTripLeg(
          passengerUUID,
          reservationReferenceID,
          tripLegReferenceID,
        ),
      );
      dispatch(mergeTripLegIntoReservationLookup(tripLeg));
    } catch {
      // silently ignore — the leg may not be accessible
    }
  };

export const createPassengerAsync =
  (passenger: Passenger) => (dispatch: Function) => {
    dispatch(setSavingPassenger(true));
    dispatch(setSavedPassenger(false));
    dispatch(setCurrentPassenger(null));
    passengerService.createPassenger(passenger).subscribe({
      next: (passenger) => {
        dispatch(uiSlice.setSuccessMessage('snackbar.createPassengerSuccess'));
        // dispatch(addPassenger(passenger));
        dispatch(setCurrentPassenger(passenger));
        dispatch(setSavingPassenger(false));
        dispatch(setSavedPassenger(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingPassenger(false));
        dispatch(setSavedPassenger(false));
      },
    });
  };

export const updatePassengerAsync =
  (passenger: Passenger) => (dispatch: Function) => {
    dispatch(setSavingPassenger(true));
    dispatch(setSavedPassenger(false));
    passengerService.updatePassenger(passenger).subscribe({
      next: (passenger) => {
        dispatch(uiSlice.setSuccessMessage('snackbar.updatePassengerSuccess'));
        dispatch(setCurrentPassenger(passenger));
        dispatch(setSavingPassenger(false));
        dispatch(setSavedPassenger(true));
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
        dispatch(setSavingPassenger(false));
        dispatch(setSavedPassenger(false));
      },
    });
  };

export const addPassengerReservationAddressAsync =
  (
    passengerUUID: string,
    address: PassengerAddress,
    passengerAddressUseBC: string,
    label: string,
  ) =>
  (dispatch: Function) => {
    passengerService
      .createPassengerAddress(
        passengerUUID,
        address,
        passengerAddressUseBC,
        label,
      )
      .subscribe({
        next: (passenger) => {
          dispatch(setCurrentPassenger(passenger));
          dispatch(
            uiSlice.setSuccessMessage('snackbar.addPassengerAddressSuccess'),
          );
        },
        error: (err) => {
          dispatch(uiSlice.setErrorMessage(err.message));
        },
      });
  };

export const deletePassengerReservationAddressAsync =
  (passengerUUID: string, label: string) => (dispatch: Function) => {
    passengerService.deletePassengerAddress(passengerUUID, label).subscribe({
      next: (passenger) => {
        dispatch(setCurrentPassenger(passenger));
        dispatch(
          uiSlice.setSuccessMessage('snackbar.deletePassengerAddressSuccess'),
        );
      },
      error: (err) => {
        dispatch(uiSlice.setErrorMessage(err.message));
      },
    });
  };

export const selectPassengerSearchClientBC = (state: {
  passenger: PassengerState;
}) => state.passenger.passengerSearchClientBC;
export const selectPassengerSearchText = (state: {
  passenger: PassengerState;
}) => state.passenger.passengerSearchText;
export const selectPassengerSearchPage = (state: {
  passenger: PassengerState;
}) => state.passenger.passengerSearchPage;
export const selectPassengerSearchPageSize = (state: {
  passenger: PassengerState;
}) => state.passenger.passengerSearchPageSize;
export const selectPassengers = (state: { passenger: PassengerState }) =>
  state.passenger.passengers;
export const selectLoadingPassengers = (state: { passenger: PassengerState }) =>
  state.passenger.loadingPassengers;
export const selectLoadedPassengers = (state: { passenger: PassengerState }) =>
  state.passenger.loadedPassengers;
export const selectCurrentPassenger = (state: { passenger: PassengerState }) =>
  state.passenger.currentPassenger;
export const selectLoadingCurrentPassenger = (state: {
  passenger: PassengerState;
}) => state.passenger.loadingCurrentPassenger;
export const selectLoadedCurrentPassenger = (state: {
  passenger: PassengerState;
}) => state.passenger.loadedCurrentPassenger;
export const selectCurrentPassengerPreferences = (state: {
  passenger: PassengerState;
}) => state.passenger.currentPassengerPreferences;
export const selectLoadingCurrentPassengerPreferences = (state: {
  passenger: PassengerState;
}) => state.passenger.loadingCurrentPassengerPreferences;
export const selectLoadedCurrentPassengerPreferences = (state: {
  passenger: PassengerState;
}) => state.passenger.loadedCurrentPassengerPreferences;
export const selectSavingCurrentPassengerPreferences = (state: {
  passenger: PassengerState;
}) => state.passenger.savingCurrentPassengerPreferences;
export const selectSavedCurrentPassengerPreferences = (state: {
  passenger: PassengerState;
}) => state.passenger.savedCurrentPassengerPreferences;
export const selectCurrentPassengerPastReservations = (state: {
  passenger: PassengerState;
}) => state.passenger.currentPassengerPastReservations;
export const selectLoadingCurrentPassengerPastReservations = (state: {
  passenger: PassengerState;
}) => state.passenger.loadingCurrentPassengerPastReservations;
export const selectLoadedCurrentPassengerPastReservations = (state: {
  passenger: PassengerState;
}) => state.passenger.loadedCurrentPassengerPastReservations;
export const selectCurrentPassengerPastReservationsNextPageData = (state: {
  passenger: PassengerState;
}) => state.passenger.currentPassengerPastReservationsNextPageData;
export const selectCurrentPassengerFutureReservations = (state: {
  passenger: PassengerState;
}) => state.passenger.currentPassengerFutureReservations;
export const selectLoadingCurrentPassengerFutureReservations = (state: {
  passenger: PassengerState;
}) => state.passenger.loadingCurrentPassengerFutureReservations;
export const selectLoadedCurrentPassengerFutureReservations = (state: {
  passenger: PassengerState;
}) => state.passenger.loadedCurrentPassengerFutureReservations;
export const selectCurrentPassengerFutureReservationsNextPageData = (state: {
  passenger: PassengerState;
}) => state.passenger.currentPassengerFutureReservationsNextPageData;
export const selectCurrentPassengerReservationLookup = (state: {
  passenger: PassengerState;
}) => state.passenger.currentPassengerReservationLookup;
export const selectLoadingCurrentPassengerReservationLookup = (state: {
  passenger: PassengerState;
}) => state.passenger.loadingCurrentPassengerReservationLookup;
export const selectLoadedCurrentPassengerReservationLookup = (state: {
  passenger: PassengerState;
}) => state.passenger.loadedCurrentPassengerReservationLookup;
export const selectSavingPassenger = (state: { passenger: PassengerState }) =>
  state.passenger.savingPassenger;
export const selectSavedPassenger = (state: { passenger: PassengerState }) =>
  state.passenger.savedPassenger;
export const selectPassengerEventEmitted = (state: {
  passenger: PassengerState;
}) => state.passenger.passengerEventEmitted;

export const selectPassengerPhoneSearch = (state: {
  passenger: PassengerState;
}) => state.passenger.passengerPhoneSearch;

export const selectPassengerSearchResetToken = (state: {
  passenger: PassengerState;
}) => state.passenger.passengerSearchResetToken;

export default slice.reducer;
